import React, { useEffect, useState } from "react";
import PartnerLayout, { tenantSlugFor, usePartnerActiveTenant } from "@/components/PartnerLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export default function Configuracion() {
  const { user } = useAuth();
  const activeSlug = usePartnerActiveTenant(user);
  const slug = activeSlug || tenantSlugFor(user);
  const [tenant, setTenant] = useState(null);
  const [primary, setPrimary] = useState("#DC5A0F");
  const [secondary, setSecondary] = useState("#0B0B0F");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get(`/tenants/${slug}/public`).then((r) => {
      setTenant(r.data);
      setPrimary(r.data.branding?.primary_color || "#DC5A0F");
      setSecondary(r.data.branding?.secondary_color || "#0B0B0F");
    }).catch(() => {});
  }, [slug]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/partner/${slug}/branding`, { primary_color: primary, secondary_color: secondary });
      toast.success("Branding actualizado. Recarga para ver cambios completos.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/partner/${slug}/branding/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      setTenant({ ...tenant, branding: r.data.branding });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PartnerLayout user={user}>
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Configuración</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Branding del tenant</h1>
        <p className="mt-2 text-gray-400">Personaliza los colores y logo del portal para tu institución.</p>

        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="p-6 bg-[#111827] border border-gray-800 rounded-xl space-y-4" data-testid="partner-branding-form">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400">Color primario</label>
              <div className="mt-1 flex gap-2">
                <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)}
                  data-testid="partner-branding-primary-color" className="h-10 w-16 bg-transparent" />
                <input type="text" value={primary} onChange={(e) => setPrimary(e.target.value)}
                  className="flex-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm font-mono text-white" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400">Color secundario</label>
              <div className="mt-1 flex gap-2">
                <input type="color" value={secondary} onChange={(e) => setSecondary(e.target.value)}
                  data-testid="partner-branding-secondary-color" className="h-10 w-16 bg-transparent" />
                <input type="text" value={secondary} onChange={(e) => setSecondary(e.target.value)}
                  className="flex-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm font-mono text-white" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400">Logo (max 2 MB)</label>
              <label className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5 text-sm cursor-pointer">
                <Upload className="w-4 h-4" /> Subir logo
                <input type="file" hidden accept="image/*" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="partner-branding-logo-input" />
              </label>
              {tenant?.branding?.logo_url && (
                <img src={`${BACKEND_URL}${tenant.branding.logo_url}`} alt="logo" className="mt-3 h-12 rounded border border-gray-800 bg-white p-1" />
              )}
            </div>
            <button onClick={save} disabled={saving} data-testid="partner-branding-save-btn"
              className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
              {saving ? "Guardando..." : "Guardar branding"}
            </button>
          </div>

          <div className="p-6 rounded-xl border border-gray-800" style={{ background: primary + "20", borderColor: primary + "60" }} data-testid="branding-preview">
            <p className="text-xs uppercase tracking-widest text-gray-300 font-semibold">Vista previa</p>
            <div className="mt-4 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: primary }} />
              <span className="text-white font-bold uppercase text-xs tracking-widest">{tenant?.name}</span>
            </div>
            <div className="mt-4 space-y-2">
              <div className="h-2 rounded" style={{ background: primary }} />
              <div className="h-2 rounded w-2/3" style={{ background: secondary }} />
              <div className="h-2 rounded w-1/3 bg-gray-700" />
            </div>
            <button className="mt-4 px-4 py-2 rounded-md text-white font-semibold text-sm" style={{ background: primary }}>
              Botón acento
            </button>
          </div>
        </div>
      </div>
    </PartnerLayout>
  );
}
